import * as React from 'react';
import { Alert, Button, Col, Nav, NavItem, NavLink, Row } from 'reactstrap';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { DataCell, DataRow, DataTable, DataTableButton } from 'components/basic/DataTable';
import { ConversionReportArchiveClient, ConversionReportArchiveItemModel } from 'client/ApiClient';

interface ArchivedConversionReportDownloadPageArgs {
    conversionId: string;
}

interface ArchivedConversionReportDownloadPageLocationState {
    conversionName?: string;
    conversionType?: string;
}

interface ArchivedConversionReportDownloadPageState {
    isLoading: boolean;
    error: string | null;
    archivedItems: ConversionReportArchiveItemModel[];
}

// Dedicated, standalone reports-download page reached only from the global archived Reports list.
// Unlike the per-conversion "Reports" tab (which lives inside the full ConversionTabsPage), this page
// shows nothing but the archived reports/mappings for the selected conversion.
class ArchivedConversionReportDownloadPage extends React.Component<RouteComponentProps<ArchivedConversionReportDownloadPageArgs>, ArchivedConversionReportDownloadPageState> {
    private readonly archiveClient = new ConversionReportArchiveClient();
    private conversionId: string = '';

    constructor(props: Readonly<RouteComponentProps<ArchivedConversionReportDownloadPageArgs>>) {
        super(props);
        this.state = {
            isLoading: true,
            error: null,
            archivedItems: [],
        };
    }

    componentDidMount() {
        this.conversionId = this.props.match.params.conversionId;
        this.loadArchivedItems();
    }

    private loadArchivedItems = () => {
        this.archiveClient.getArchivedItems(this.conversionId)
            .then(archivedItems => this.setState({ isLoading: false, archivedItems: archivedItems || [] }))
            .catch(error => {
                console.log(error);
                this.setState({ isLoading: false, error: 'Failed to load archived conversion reports. Please try again.' });
            });
    };

    downloadArchived = (reportKey: string) => {
        this.archiveClient.downloadArchivedFile(this.conversionId, reportKey)
            .then(response => saveAs(response!.data, response!.fileName))
            .catch(error => { console.log(error) });
    }

    downloadAllArchived = (category: string) => {
        this.state.archivedItems
            .filter(item => item.category === category)
            .forEach(item => {
                this.archiveClient.downloadArchivedFile(this.conversionId, item.reportKey!)
                    .then(response => saveAs(response!.data, response!.fileName))
                    .catch(error => { console.log(error) });
            });
    }

    public render() {
        const { isLoading, error, archivedItems } = this.state;

        if (isLoading) {
            return null;
        }

        const hasReports = archivedItems.some(item => item.category === 'Report' && item.hasData);
        const hasMappings = archivedItems.some(item => item.category === 'Mapping' && item.hasData);
        const hasAnyData = hasReports || hasMappings;
        // The live conversion's Reports tab never lists the mapping export as a row - only reports.
        const reportItems = archivedItems.filter(item => item.category === 'Report');

        const { conversionName, conversionType } = (this.props.location.state as ArchivedConversionReportDownloadPageLocationState) || {};
        const titleSuffix = [conversionName, conversionType].filter(Boolean).join(' - ');

        return (
            <>
                <Nav tabs>
                    <NavItem>
                        <NavLink className='active'>Reports</NavLink>
                    </NavItem>
                </Nav>
                <Row>
                    <Col>
                        <h3 className='mapping-body-header'>
                            {titleSuffix || 'Reports'}
                        </h3>
                    </Col>
                    <Col>
                        {hasReports &&
                            <Button
                                className='submit-button-orange float-right'
                                onClick={() => this.downloadAllArchived('Report')}>
                                Download all reports
                            </Button>
                        }
                        {hasMappings &&
                            <Button
                                className='submit-button-orange float-right'
                                style={{ marginRight: '10px' }}
                                onClick={() => this.downloadAllArchived('Mapping')}>
                                Download all mappings
                            </Button>
                        }
                    </Col>
                </Row>
                {error && <Alert color='danger'>{error}</Alert>}
                {!error && !hasAnyData && <p>No reports and mappings are available for this conversion.</p>}
                {!error && hasReports &&
                    <DataTable>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th className='controls-column'></th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportItems.map(item => (
                                <DataRow id={item.id} key={item.id}>
                                    <DataCell>{item.reportName}</DataCell>
                                    <DataCell>
                                        <DataTableButton icon='file_download' id={item.id} title='Download'
                                            action={() => this.downloadArchived(item.reportKey!)} />
                                    </DataCell>
                                </DataRow>
                            ))}
                        </tbody>
                    </DataTable>
                }
            </>
        );
    }
}

export default withRouter(ArchivedConversionReportDownloadPage);
